import { ApiRequestParams, DataTable, DataTableColumn, DataTableFilter } from "@/components/DataTable";
import { QuickBooksInvoiceType } from "@/interfaces";
import { QuickBooksInvoiceDetailsModal } from "@/modules/Client/Invoices";
import api from "@/services/api";
import { getQbInvoiceById } from "@/services/quickbooksInvoices";
import { useUserStore } from "@/stores/userStore";
import { formatCompanyDate } from "@/utils/dateUtils";
import { Button, Card, message, Spin, Tag } from "antd";
import dayjs from "dayjs";
import { DollarSign, Receipt } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Payment type definition
 */
interface Payment {
  id: string;
  clientId: string;
  companyId: string;
  qbInvoiceId: string | null;
  nsInvoiceId: string | null;
  invoiceNumber: string;
  amount: number;
  date: string;
  currency: string;
  initiatedBy: string;
  status: "succeeded" | "pending" | "failed" | "refunded";
  clientName: string;
}

interface InvoiceWithStatus extends QuickBooksInvoiceType {
  _computedStatus: "Paid" | "Open" | "Overdue";
}

const getInvoiceStatus = (invoice: QuickBooksInvoiceType): "Paid" | "Open" | "Overdue" => {
  if (invoice.Balance === 0) return "Paid";
  const dueDate = new Date(invoice.DueDate);
  const today = new Date();
  if (dueDate < today) return "Overdue";
  return "Open";
};

/**
 * StripePayment component for viewing payment transactions
 * Displays all payment history with invoice details for employee assigned clients
 */
const StripePayment = () => {
  const dateFormat = useUserStore((state) => state.currentCompany?.dateFormat);
  const currentCompany = useUserStore((state) => state.currentCompany);
  const currentCompanyId = currentCompany?.id;

  // Modal state
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithStatus | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const defaultDateRange = useMemo(() => [dayjs().subtract(2, "month"), dayjs()], []);
  const lastFetchedDateRangeRef = useRef<string>("");

  const formatFilterDate = (value: unknown): string | undefined => {
    if (!value) return undefined;
    if ((value as { format?: (fmt: string) => string }).format) {
      return (value as { format: (fmt: string) => string }).format("YYYY-MM-DD");
    }
    if (value instanceof Date) {
      return dayjs(value).format("YYYY-MM-DD");
    }
    if (typeof value === "string") return value;
    return undefined;
  };

  const mapApiPayment = (payment: any): Payment => ({
    clientId: payment.clientId,
    companyId: payment.companyId,
    id: payment.id,
    qbInvoiceId: payment.qbInvoiceId,
    nsInvoiceId: payment.nsInvoiceId,
    invoiceNumber: payment?.invoiceApproval?.qbInvoiceNumber || "N/A",
    amount: Number(payment.amount) || 0,
    date: payment.createdAt,
    currency: payment.currency,
    initiatedBy: `${payment?.initiatedBy?.firstName || "NA"} ${payment?.initiatedBy?.lastName || ""}`.trim(),
    status: payment.status,
    clientName: payment?.client?.companyName || "N/A",
  });

  const fetchPaymentsForDateRange = useCallback(
    async (dateStart: string, dateEnd: string) => {
      if (!currentCompanyId) {
        setPayments([]);
        return;
      }

      setPaymentsLoading(true);
      try {
        const allRows: Payment[] = [];
        let page = 1;
        let total = 0;

        while (true) {
          const response = await api.get("/payments/employee-clients", {
            params: {
              companyId: currentCompanyId,
              status: "all",
              dateStart,
              dateEnd,
              page,
            },
          });

          const pageRows = Array.isArray(response.data?.data) ? response.data.data : [];
          if (page === 1) {
            total = Number(response.data?.total) || pageRows.length;
          }

          allRows.push(...pageRows.map(mapApiPayment));

          if (pageRows.length === 0 || allRows.length >= total) {
            break;
          }

          page += 1;
        }

        setPayments(allRows);
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        message.error(err.response?.data?.message || "Failed to fetch payments");
        setPayments([]);
      } finally {
        setPaymentsLoading(false);
      }
    },
    [currentCompanyId],
  );

  useEffect(() => {
    const defaultStart = defaultDateRange[0].format("YYYY-MM-DD");
    const defaultEnd = defaultDateRange[1].format("YYYY-MM-DD");
    const defaultKey = `${defaultStart}|${defaultEnd}`;

    lastFetchedDateRangeRef.current = defaultKey;
    fetchPaymentsForDateRange(defaultStart, defaultEnd);
  }, [currentCompanyId, fetchPaymentsForDateRange, defaultDateRange]);

  const handleTableChange = useCallback(
    (params: ApiRequestParams) => {
      const dateRange = params.filters?.date as unknown[] | undefined;
      const defaultStart = defaultDateRange[0].format("YYYY-MM-DD");
      const defaultEnd = defaultDateRange[1].format("YYYY-MM-DD");

      const nextStart =
        Array.isArray(dateRange) && dateRange.length === 2
          ? formatFilterDate(dateRange[0]) || defaultStart
          : defaultStart;
      const nextEnd =
        Array.isArray(dateRange) && dateRange.length === 2 ? formatFilterDate(dateRange[1]) || defaultEnd : defaultEnd;

      const nextKey = `${nextStart}|${nextEnd}`;
      if (nextKey === lastFetchedDateRangeRef.current) {
        return;
      }

      lastFetchedDateRangeRef.current = nextKey;
      fetchPaymentsForDateRange(nextStart, nextEnd);
    },
    [defaultDateRange, fetchPaymentsForDateRange],
  );

  const totalTransactions = payments.length;
  const totalPaid = useMemo(
    () =>
      payments
        .filter((payment) => payment.status === "succeeded")
        .reduce((sum, payment) => sum + Number(payment.amount), 0),
    [payments],
  );

  /**
   * Define columns for DataTable
   */
  const columns: DataTableColumn<Payment>[] = [
    {
      key: "invoiceNumber",
      title: "Invoice Number",
      dataIndex: "invoiceNumber",
      sortable: true,
      render: (value: unknown) => {
        const invoice = value as string;
        return <span className="font-semibold text-blue-600">{invoice}</span>;
      },
    },
    {
      key: "clientName",
      title: "Client Name",
      dataIndex: "clientName",
      sortable: true,
      render: (value: unknown) => {
        const invoice = value as string;
        return <span>{invoice}</span>;
      },
    },
    {
      key: "initiatedBy",
      title: "Initiated By",
      dataIndex: "initiatedBy",
      sortable: true,
      render: (value: unknown) => {
        const invoice = value as string;
        return <span>{invoice}</span>;
      },
    },
    {
      key: "date",
      title: "Date",
      dataIndex: "date",
      sortable: true,
      render: (value: unknown) => formatCompanyDate(value as string, dateFormat),
    },
    {
      key: "amount",
      title: "Amount",
      dataIndex: "amount",
      sortable: true,
      render: (value: unknown) => {
        const amount = value as number;
        return <span className="font-bold text-gray-900">${amount.toLocaleString()}</span>;
      },
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      sortable: true,
      render: (value: unknown) => {
        const status = value as "succeeded" | "pending" | "failed" | "refunded";
        const colorMap = {
          succeeded: "success",
          pending: "warning",
          failed: "error",
          refunded: "default",
        };
        return <Tag color={colorMap[status]}>{status}</Tag>;
      },
    },
  ];

  /**
   * Define filters for DataTable
   */
  const filters: DataTableFilter[] = [
    {
      label: "Status",
      key: "status",
      type: "select",
      placeholder: "All Status",
      options: [
        { label: "Succeeded", value: "succeeded" },
        { label: "Failed", value: "failed" },
        { label: "Refunded", value: "refunded" },
        { label: "Pending", value: "pending" },
      ],
    },
    {
      label: "Payment Date Range",
      key: "date",
      type: "dateRange",
      placeholder: "Filter by payment date",
      defaultValue: defaultDateRange,
    },
  ];

  /**
   * Custom action column renderer
   */
  const actionColumn = (record: Payment) => (
    <Button
      type="primary"
      size="small"
      icon={<Receipt size={14} />}
      onClick={() => {
        if (!record.qbInvoiceId) {
          message.error("No associated QuickBooks invoice found for this payment.");
          return;
        }
        handleViewInvoice(record.qbInvoiceId);
      }}
    >
      View Invoice
    </Button>
  );

  /**
   * Opens invoice details modal
   */
  const handleViewInvoice = async (invoiceId: string) => {
    if (!currentCompany?.id) return;
    try {
      setInvoiceLoading(true);
      const invoice = await getQbInvoiceById(invoiceId, currentCompany.id);

      setSelectedInvoice({
        ...invoice.data,
        _computedStatus: getInvoiceStatus(invoice.data),
      });
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching invoice details: ", error);
    } finally {
      setInvoiceLoading(false);
    }
  };

  /**
   * Handles downloading invoice
   */
  /**
   * Handle PDF download
   */
  const handleDownloadPdf = async (invoice: QuickBooksInvoiceType) => {
    if (!invoice.Id) {
      message.warning("PDF not available for this invoice");
      return;
    }

    setDownloadingPdf(invoice.Id);
    try {
      const response = await api.get("/quickbooks/invoice/pdf", {
        params: { invoiceId: invoice.Id, companyId: currentCompany?.id },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${invoice.DocNumber || invoice.Id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("PDF downloaded successfully");
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || "Failed to download PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  return (
    <div className="h-full bg-gray-50 p-6">
      {/* Header Section */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Stripe Payment Processing</h1>
          <p className="text-gray-600">Monitor and manage Stripe payment transactions</p>
          <p className="text-sm text-gray-500 mt-1">
            Transaction history data on this page is fetched locally for payments done via this platform only.
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Total Paid */}
        <div className="bg-white rounded-xl p-6 py-8 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium mb-2">Total Paid</p>
              <h3 className="text-4xl font-bold text-gray-900">${totalPaid.toLocaleString()}</h3>
              <p className="text-xs text-gray-500 mt-1">All successful payments</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <DollarSign size={20} className="text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Total Transactions */}
        <div className="bg-white rounded-xl p-6 py-8 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-500 text-sm font-medium mb-2">Total Transactions</p>
              <h3 className="text-4xl font-bold text-gray-900">{totalTransactions}</h3>
              <p className="text-xs text-gray-500 mt-1">Past transactions</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <Receipt size={20} className="text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payments Table Card */}
      <Card className="bg-white shadow-sm" bodyStyle={{ padding: "24px" }}>
        <DataTable<Payment>
          columns={columns}
          data={payments}
          loading={paymentsLoading}
          searchable
          searchPlaceholder="Search transactions..."
          filters={filters}
          onChange={handleTableChange}
          actionColumn={actionColumn}
          actionColumnTitle="Actions"
          exportOptions={{
            csv: true,
            excel: true,
            filename: "payment-history-export",
          }}
          pagination
          defaultPageSize={10}
          rowKey="id"
        />
      </Card>

      <Spin spinning={invoiceLoading} tip="Fetching invoice..." fullscreen></Spin>
      {/* Invoice Details Modal */}
      <QuickBooksInvoiceDetailsModal
        open={modalOpen}
        invoice={selectedInvoice}
        status={selectedInvoice?._computedStatus}
        onClose={() => {
          setModalOpen(false);
          setSelectedInvoice(null);
        }}
        dateFormat={dateFormat}
        onDownloadPdf={handleDownloadPdf}
        downloadingPdf={downloadingPdf}
      />
    </div>
  );
};

export default StripePayment;
